import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {post, get} from "../../utils/fetchAPI";

const getStoredUser = () => {
    try {
        const stored = localStorage.getItem("user");
        if (!stored) return null;
        
        const parsed = JSON.parse(stored);
        // Handle both cases: { admin: {...} } or plain object
        if (parsed?.admin?._id) {
            return parsed.admin;
        }
        return parsed?._id ? parsed : null;
    } catch (e) {
        return null;
    }
};

const initialState = {
    user: getStoredUser(),
    token: localStorage.getItem("token") || "",
    permissions: JSON.parse(localStorage.getItem("permissions") || "[]"),
    // userRoles: "" || JSON.parse(localStorage.getItem("userRoles")),
    isLoggedIn: !!localStorage.getItem("token") && !!getStoredUser(),
    isLoading: false,
    authError: null,
    selectedRole: JSON.parse(localStorage.getItem("selectedRole") || "null"),
    organization: []
};

export const login = createAsyncThunk(
    "auth/login",
    async (payload, {rejectWithValue}) => {
        try {
            const response = await post("/login", payload);
            if (response?.status === 200) return response.data;
            else return rejectWithValue(response.data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchAdminDetails = createAsyncThunk(
    "auth/fetchAdminDetails",
    async (adminId, {rejectWithValue}) => {
        try {
            const response = await get(`/admin/details/${adminId}`);
            if (response?.status === 200) {
                console.log("response", response);
                const adminData = response.data?.result?.admin;
                
                // Extract permission IDs from the response
                const permissionIds = response.data?.result?.permissions?.map(p => p.permission) || [];
                
                // Fetch full permission details for each permission ID
                const permissionsResponse = await get('/permission/list');
                const allPermissions = permissionsResponse.data?.permissions || [];
                
                // Filter permissions based on the IDs we got from admin details
                const userPermissions = allPermissions.filter(perm => 
                    permissionIds.includes(perm._id)
                );
                
                // If role is just an ID string, fetch the full role details
                let adminWithRole = { ...adminData };
                if (adminData?.role && typeof adminData.role === 'string') {
                    try {
                        const roleResponse = await get(`/role/${adminData.role}`);
                        if (roleResponse?.status === 200 && roleResponse.data?._id) {
                            adminWithRole.role = roleResponse.data;
                        }
                    } catch (roleError) {
                        console.error("Error fetching role details:", roleError);
                        // Keep role as ID if fetch fails
                    }
                }
                
                return {
                    admin: adminWithRole,
                    permissions: userPermissions
                };
            } else {
                return rejectWithValue(response.data);
            }
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch admin details" });
        }
    }
);

// export const registerUser = createAsyncThunk(
//   "auth/register",
//   async (payload, { rejectWithValue }) => {
//     try {
//       const response = await post("user/create", payload);
//       if (response?.status === 201) {
//         return response.data;
//       } else {
//         return rejectWithValue(response.data);
//       }
//     } catch (error) {
//       return rejectWithValue(error.response.data);
//     }
//   }
// );


const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        removeAuthInfo: (state, action) => {
            state.user = "";
            state.userRoles = "";
            state.token = "";
            state.permissions = [];
            state.authError = null; // Clear error when logging out
            state.isLoggedIn = false;
            localStorage.removeItem("token");
            localStorage.removeItem("isLoggedIn");
            localStorage.removeItem("user");
            localStorage.removeItem("permissions");
        },
        setPermissions: (state, action) => {
            state.permissions = action.payload || [];
            localStorage.setItem("permissions", JSON.stringify(action.payload || []));
        },
        setAuthInfo: (state, action) => {
            let {token, ...admin} = action.payload
            console.log("action.payload", admin);
            // Extract admin object if nested, otherwise use as is
            const userObject = admin?.admin || admin;
            state.user = userObject;
            // console.log("action",action.payload)
            if (userObject){
                localStorage.setItem("token", token);
                localStorage.setItem("user", JSON.stringify(userObject));
            }

        },
        //login reducer to change the state
        setLogin: (state, action) => {
            let {isLoggedIn} = action.payload
            state.isLoggedIn = isLoggedIn;
            localStorage.setItem("isLoggedIn", isLoggedIn)
        },
        setOrganizationInfo: (state, action) => {
            const organizationIds = action.payload.map((org) => org._id);
            state.organization = organizationIds;
            localStorage.setItem("organizationIds", JSON.stringify(organizationIds));
        },
        clearAuthError: (state) => {
            state.authError = null;
        }
        // setSelectedRole: (state, action) => {
        //   state.selectedRole = action.payload;
        //   localStorage.setItem("selectedRole", JSON.stringify(action.payload));
        // },

    },
    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state) => {
                state.isLoading = true;
                state.authError = null; // Clear previous error
            })
            .addCase(login.fulfilled, (state, action) => {
                state.isLoading = false;
                state.authError = null; // Clear error on success
                // Handle both cases: payload.admin or payload.admin.admin
                const adminObject = action.payload.admin?.admin || action.payload.admin;
                state.user = adminObject;
                state.token = action.payload.token;
                state.permissions = action.payload.permissions || [];
                state.isLoggedIn = true;
                localStorage.setItem("user", JSON.stringify(adminObject));
                localStorage.setItem("permissions", JSON.stringify(action.payload.permissions || []));
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false;
                state.authError = action.payload?.message || "Invalid credential";
            })
            .addCase(fetchAdminDetails.fulfilled, (state, action) => {
                console.log("fetchAdminDetails fulfilled, updating permissions:", action.payload.permissions);
                state.permissions = action.payload.permissions || [];
                localStorage.setItem("permissions", JSON.stringify(action.payload.permissions || []));
                if (action.payload.admin) {
                    state.user = action.payload.admin;
                    localStorage.setItem("user", JSON.stringify(action.payload.admin));
                }
            })
            .addCase(fetchAdminDetails.rejected, (state, action) => {
                console.error("Failed to fetch admin details:", action.payload);
            });
    },

});

export default authSlice.reducer;
export const {removeAuthInfo, setAuthInfo, setSelectedRole, setLogin, setOrganizationInfo, clearAuthError, setPermissions} = authSlice.actions;
